import { useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { SendingDate } from 'src/modules/sending/models/sendingDate';
import { ControlledAutocompleteDropdown } from 'src/shared/components/form/controlledAutocompleteDropdown';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { getAllSendingDates } from 'src/shared/services/sendingService';

type SetSendingDateProps = {
  disabled?: boolean;
};

export const SetSendingDate = (props: SetSendingDateProps) => {
  const { disabled = false } = props;
  const { t } = useTranslation();
  const isMounted = useIsMounted();
  const [, setLoading] = useState<boolean>(false);
  const [sendingDates, setSendingDates] = useState<SendingDate[]>([]);
  const { setValue } = useFormContext();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const getSendingDates = await getAllSendingDates(0, 100, 'sndDate', 'asc');

      if (isMounted()) {
        setSendingDates(getSendingDates.data.results);
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
      }
    } finally {
      if (isMounted()) {
        setLoading(false);
      }
    }
  }, [isMounted]);

  useEffect(() => {
    if (isMounted()) {
      fetchData();
    }
  }, [fetchData, isMounted]);

  const formatSendingDateOptions = (dates: SendingDate[]) => {
    return dates.map((dateItem) => ({
      ...dateItem,
      formattedSndDate: DateHelper.getDate(dateItem.sndDate),
    })) as SendingDate[];
  };

  return (
    <ControlledAutocompleteDropdown<SendingDate>
      disable={disabled}
      componentWidth='100%'
      nameProp='formattedSndDate'
      name='sndDate'
      options={formatSendingDateOptions(sendingDates)}
      callback={(sndDate) => {
        const formatSndDate = sndDate ? DateHelper.getDate(sndDate.sndDate) : undefined;
        setValue('sndDate', formatSndDate);
      }}
      label={t('invoice:invoiceMissionPage.sendingDate')}
      placeholder={t('common:button.choose')}
    />
  );
};
